import gsap from "gsap";

import { playRipSound, startRiser } from "./audio";

export type IntroHandle = {
  finished: Promise<void>;
  pop: () => void;
  kill: () => void;
};

const shakes = [
  { amplitude: 3, duration: 0.07, repeat: 4 },
  { amplitude: 7, duration: 0.05, repeat: 6 },
  { amplitude: 13, duration: 0.04, repeat: 8 },
];

function centerOf(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

export function burstOffsets(
  origin: { x: number; y: number },
  slots: HTMLElement[],
) {
  return slots.map((slot) => {
    const destination = centerOf(slot);
    return { x: origin.x - destination.x, y: origin.y - destination.y };
  });
}

function whiteFlashAndShockwave(pack: HTMLElement) {
  const flash = document.createElement("div");
  flash.className = "quick-rip__body-flash";
  flash.setAttribute("aria-hidden", "true");
  document.body.append(flash);

  const shockwave = document.createElement("div");
  const origin = centerOf(pack);
  shockwave.className = "quick-rip__pack-shockwave";
  shockwave.setAttribute("aria-hidden", "true");
  shockwave.style.left = `${origin.x}px`;
  shockwave.style.top = `${origin.y}px`;
  document.body.append(shockwave);

  gsap
    .timeline({ onComplete: () => flash.remove() })
    .set(flash, { opacity: 0 })
    .to(flash, { opacity: 1, duration: 0.055, ease: "power4.in" })
    .to(flash, { opacity: 1, duration: 0.055, ease: "none" })
    .to(flash, { opacity: 0, duration: 0.31, ease: "power2.out" });
  gsap.timeline({ onComplete: () => shockwave.remove() }).fromTo(
    shockwave,
    { xPercent: -50, yPercent: -50, scale: 0.18, opacity: 0.95 },
    {
      xPercent: -50,
      yPercent: -50,
      scale: 2.4,
      opacity: 0,
      duration: 0.52,
      ease: "power3.out",
    },
  );
}

export function runQuickRipIntro({
  pack,
  slots,
  glow,
  reducedMotion,
  onFanStart,
  onFanLanded,
}: {
  pack: HTMLElement | null;
  slots: HTMLElement[];
  glow: HTMLElement | null;
  flash?: HTMLElement | null;
  reducedMotion: boolean;
  onFanStart: () => void;
  onFanLanded: () => void;
}): IntroHandle {
  if (reducedMotion || !pack) {
    gsap.set(slots, { x: 0, y: 0, rotateZ: 0, scale: 1, opacity: 1 });
    if (pack) gsap.set(pack, { opacity: 0 });
    onFanStart();
    onFanLanded();
    return {
      finished: Promise.resolve(),
      pop: () => undefined,
      kill: () => undefined,
    };
  }

  gsap.set(pack, {
    yPercent: -160,
    scale: 0.55,
    opacity: 0,
    rotateZ: -8,
  });
  gsap.set(slots, { opacity: 0, willChange: "transform" });
  if (glow) gsap.set(glow, { opacity: 0.55 });
  let popped = false;
  let killed = false;
  let settled = false;
  let resolveFinished: () => void = () => undefined;
  let fanTimeline: gsap.core.Timeline | null = null;
  let popTimeline: gsap.core.Timeline | null = null;
  const riser = startRiser();
  const build = gsap.timeline();

  const finished = new Promise<void>((resolve) => {
    resolveFinished = resolve;
  });
  const settle = () => {
    if (settled) return;
    settled = true;
    resolveFinished();
  };

  const fanCards = () => {
    onFanStart();
    void playRipSound("cards_fan", 0.9);
    // Every card starts at the physical pack centroid and flies to its already
    // fitted DOM destination. This works for 2-2-1, 3-2, and five-up without
    // assuming that presentation order matches the content array.
    const origin = centerOf(pack);
    const offsets = burstOffsets(origin, slots);
    slots.forEach((slot, index) => {
      gsap.set(slot, {
        opacity: 1,
        x: offsets[index]?.x ?? 0,
        y: offsets[index]?.y ?? 0,
        rotateZ: 0,
        scale: 0.42,
      });
    });
    fanTimeline = gsap.timeline({
      onComplete: () => {
        gsap.set(slots, { willChange: "auto" });
        onFanLanded();
        settle();
      },
    });
    fanTimeline.to(slots, {
      x: 0,
      y: 0,
      rotateZ: 0,
      scale: 1,
      duration: 0.72,
      stagger: { each: 0.055, from: "center" },
      ease: "back.out(1.45)",
    });
  };

  const reveal = () => {
    riser.stop();
    void playRipSound("pack_pop", 1);
    if ("vibrate" in navigator) navigator.vibrate([35, 20, 55]);
    whiteFlashAndShockwave(pack);
    gsap.to(pack, {
      scale: 1.5,
      opacity: 0,
      duration: 0.22,
      ease: "power3.in",
    });
    if (slots.length) fanCards();
    else settle();
  };

  const pop = () => {
    if (popped || killed) return;
    popped = true;
    build.kill();
    gsap.killTweensOf(pack);
    gsap.set(pack, {
      x: 0,
      y: 0,
      yPercent: 0,
      scale: 1,
      rotateZ: 0,
      opacity: 1,
    });
    popTimeline = gsap.timeline().to(pack, {
      scale: 0.82,
      duration: 0.1,
      ease: "power2.in",
      onComplete: reveal,
    });
  };

  void playRipSound("pack_flyin", 0.85);
  build.to(pack, {
    yPercent: 0,
    scale: 1,
    opacity: 1,
    rotateZ: 0,
    duration: 0.55,
    ease: "back.out(1.5)",
  });
  if (glow) {
    build.to(glow, { opacity: 1, duration: 0.8, ease: "power2.in" }, 0.55);
  }
  for (const shake of shakes) {
    build.to(pack, {
      x: `random(${-shake.amplitude}, ${shake.amplitude})`,
      y: `random(${-shake.amplitude}, ${shake.amplitude})`,
      duration: shake.duration,
      repeat: shake.repeat,
      yoyo: true,
      ease: "none",
    });
  }
  build.set(pack, { x: 0, y: 0 }).call(pop);

  return {
    finished,
    pop,
    kill() {
      killed = true;
      build.kill();
      popTimeline?.kill();
      fanTimeline?.kill();
      riser.stop();
      gsap.killTweensOf(pack);
      gsap.killTweensOf(slots);
      gsap.set(slots, { willChange: "auto" });
    },
  };
}

export function spawnCardBurst(container: HTMLElement, rainbow: boolean) {
  for (let index = 0; index < 18; index += 1) {
    const particle = document.createElement("i");
    particle.className = "quick-rip-particle";
    particle.style.setProperty(
      "--particle-color",
      rainbow
        ? `hsl(${Math.round((index / 18) * 360)} 100% 68%)`
        : index % 2
          ? "#f4d777"
          : "#6de5ff",
    );
    container.append(particle);
    const angle = (Math.PI * 2 * index) / 18;
    const distance = 70 + (index % 5) * 20;
    gsap.fromTo(
      particle,
      { x: 0, y: 0, scale: 1, opacity: 1 },
      {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        scale: 0,
        opacity: 0,
        duration: 0.7 + (index % 3) * 0.1,
        ease: "power3.out",
        onComplete: () => particle.remove(),
      },
    );
  }
}
